import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { api } from '../api/client.js'
import { getSocket } from '../socket/client.js'
import { normalizeConversationStatus } from '../components/conversations/ConversationInbox.js'
import type { ConversationItem, InboxFilter } from '../components/conversations/ConversationInbox.js'
import { formatCustomerName, cleanIdentifier } from '../utils/formatCustomerName.js'

/**
 * Loads conversations from GET /conversations and maps them to the inbox
 * component's ConversationItem shape. Used by the agent inbox pages so that
 * conversations created via the New Conversation form actually appear.
 *
 * Owns filter/search/date-range/page state and drives every fetch off the
 * server (GET /conversations + GET /conversations/counts) — required at
 * scale, since filtering only the currently-fetched page client-side
 * silently broke search/filters once there were more conversations than
 * that page held.
 */
export interface ApiConversation {
  id: string
  referenceId: string
  channelOrigin: string
  status: string
  primaryHandle: string | null
  displayName: string | null
  lastMessagePreview: string
  lastMessageAt: string | null
  openedAt: string | null
  slaStartedAt: string | null
  slaState: string
  assignedAgentId: string | null
  assignedAgentLoginId: string | null
  subject: string | null
  source: string | null
  isRead: boolean
  isStarred: boolean
  isArchived: boolean
  priorityLabel: string | null
  tags: string[]
  csatScore: number | null
  transferCount: number
  parentId: string | null
  isParent: boolean
  parentReferenceId: string | null
  childCount: number
  childReferenceIds: string[]
  mergedIntoId: string | null
  mergedIntoReferenceId: string | null
  secondaryCount: number
  secondaryReferenceIds: string[]
  typeLabel: string | null
  stateLabel: string | null
  conversationStatusId: string | null
  statusLabel: string | null
  statusColor: string | null
  urgencyLabel: string | null
  impactLabel: string | null
  categoryLabel: string | null
  subcategoryLabel: string | null
  locationLabel: string | null
  branchLabel: string | null
  departmentLabel: string | null
  campaignLabel: string | null
  teamLabel: string | null
  firstResponseDueAt: string | null
  resolutionDueAt: string | null
  firstRespondedAt: string | null
  resolvedAt: string | null
  lastModifiedAt: string | null
  lastModifiedBy: string | null
}

interface ApiCounts {
  all: number
  unread: number
  read: number
  starred: number
  closed: number
  nearingSla: number
  beyondSla: number
  archived: number
  /** Non-archived count per catalog status id (`conversation_status`). */
  byStatus?: Record<string, number>
  /** Name/colour of every status that has conversations (incl. deactivated ones). */
  statusInfo?: Array<{ id: string; name: string; color: string | null }>
}

const EMPTY_COUNTS: ApiCounts = { all: 0, unread: 0, read: 0, starred: 0, closed: 0, nearingSla: 0, beyondSla: 0, archived: 0, byStatus: {} }

// Human-friendly labels for known source codes (mirrors KC2 INTERACTION_SOURCES).
const SOURCE_LABELS: Record<string, string> = {
  AGENT_PORTAL: 'Agent Portal',
  CLIENT_PORTAL: 'Client Portal',
  EMAIL: 'Email',
  SMS: 'SMS',
  WHATSAPP: 'WhatsApp',
  FACEBOOK: 'Facebook',
  TWITTER: 'Twitter',
  YOUTUBE: 'YouTube',
  CALL: 'Call',
  WALKIN: 'Walk-in',
  web: 'Agent Portal',
}

function sourceLabel(s: string | null): string | undefined {
  if (!s) return undefined
  return SOURCE_LABELS[s] ?? s
}

const CHANNELS = new Set(['email', 'sms', 'whatsapp', 'telegram', 'facebook', 'instagram', 'chat', 'voice', 'web', 'youtube', 'x'])

function mapSla(s: string): ConversationItem['slaStatus'] {
  if (s === 'nearing') return 'nearing'
  if (s === 'breached') return 'breached'
  return 'ok'
}

function mapChannel(c: string): ConversationItem['channel'] {
  return (CHANNELS.has(c) ? c : 'chat') as ConversationItem['channel']
}

// Tenants define their own priority labels (e.g. "P1 - Critical"), so this
// buckets by keyword rather than an exact match. Returns undefined (badge
// omitted) instead of guessing when no priority has been set or the label
// doesn't map to a known bucket — showing a fabricated "Medium" on every
// conversation was actively misleading.
function mapPriority(label: string | null): ConversationItem['priority'] {
  if (!label) return undefined
  const l = label.toLowerCase()
  if (/urgent|critical|p1/.test(l)) return 'urgent'
  if (/high|p2/.test(l)) return 'high'
  if (/medium|normal|p3/.test(l)) return 'medium'
  if (/low|p4/.test(l)) return 'low'
  return undefined
}

// Legacy auto-created contacts were named with noisy placeholders (e.g.
// "Email Customer - x@y.com", "Phone Customer - +1555…", "Customer - chat -
// abc123") before the identity resolver was fixed to just store the raw
// handle. Strip those prefixes at display time so old data reads cleanly too
// — if a real name is ever set on the contact, it won't match this pattern
// and passes through unchanged.
const LEGACY_NAME_PREFIX = /^(?:Email|Phone|SMS|WhatsApp)?\s*Customer\s*-\s*(?:[a-z]+\s*-\s*)?/i

function cleanDisplayName(name: string): string {
  const stripped = name.replace(LEGACY_NAME_PREFIX, '').trim()
  return stripped || name
}

export function toItem(r: ApiConversation): ConversationItem {
  const cleanName = formatCustomerName(r.displayName, r.primaryHandle, r.channelOrigin)
  const cleanHandle = r.primaryHandle ? cleanIdentifier(r.primaryHandle) : undefined
  return {
    id: r.id,
    customerName: cleanName,
    customerHandle: cleanHandle,
    customerAvatar: undefined,
    subject: r.subject || '(No subject)',
    preview: r.lastMessagePreview || '',
    channel: mapChannel(r.channelOrigin),
    status: normalizeConversationStatus(r.status),
    priority: mapPriority(r.priorityLabel),
    slaStatus: mapSla(r.slaState),
    isRead: r.isRead,
    isStarred: r.isStarred,
    isArchived: r.isArchived,
    // Must be stable across refetches: the inbox flags a row as just-updated
    // when its timestamp moves, so a `new Date()` fallback would make every
    // row look freshly changed on every poll.
    timestamp: r.lastMessageAt || r.openedAt || '',
    createdAt: r.openedAt || undefined,
    slaStartedAt: r.slaStartedAt ?? undefined,
    assignedTo: r.assignedAgentLoginId || undefined,
    assignedAgentId: r.assignedAgentId ?? undefined,
    interactionId: r.referenceId,
    source: sourceLabel(r.source),
    tags: Array.isArray(r.tags) ? r.tags : [],
    csatScore: r.csatScore,
    transferCount: r.transferCount ?? 0,
    isParentTicket: r.isParent ?? false,
    parentId: r.parentId ?? undefined,
    parentReferenceId: r.parentReferenceId ?? undefined,
    childCount: r.childCount ?? 0,
    childReferenceIds: Array.isArray(r.childReferenceIds) ? r.childReferenceIds : [],
    mergedIntoId: r.mergedIntoId ?? undefined,
    mergedIntoReferenceId: r.mergedIntoReferenceId ?? undefined,
    secondaryCount: r.secondaryCount ?? 0,
    secondaryReferenceIds: Array.isArray(r.secondaryReferenceIds) ? r.secondaryReferenceIds : [],
    typeLabel: r.typeLabel ?? undefined,
    stateLabel: r.stateLabel ?? undefined,
    conversationStatusId: r.conversationStatusId ?? undefined,
    statusLabel: r.statusLabel ?? undefined,
    statusColor: r.statusColor ?? undefined,
    urgencyLabel: r.urgencyLabel ?? undefined,
    impactLabel: r.impactLabel ?? undefined,
    categoryLabel: r.categoryLabel ?? undefined,
    subcategoryLabel: r.subcategoryLabel ?? undefined,
    locationLabel: r.locationLabel ?? undefined,
    branchLabel: r.branchLabel ?? undefined,
    departmentLabel: r.departmentLabel ?? undefined,
    campaignLabel: r.campaignLabel ?? undefined,
    teamLabel: r.teamLabel ?? undefined,
    firstResponseDueAt: r.firstResponseDueAt ?? undefined,
    resolutionDueAt: r.resolutionDueAt ?? undefined,
    firstRespondedAt: r.firstRespondedAt ?? undefined,
    resolvedAt: r.resolvedAt ?? undefined,
    lastModifiedAt: r.lastModifiedAt ?? undefined,
    lastModifiedBy: r.lastModifiedBy ?? undefined,
  }
}

/** Whether a locally-mutated item still belongs in the active filter tab —
 *  used to drop rows from view immediately (e.g. marking read while viewing
 *  "Unread") without waiting on a full refetch. */
function matchesFilter(c: ConversationItem, filter: InboxFilter): boolean {
  if (filter === 'archived') return c.isArchived
  if (c.isArchived) return false
  if (filter === 'unread') return !c.isRead
  if (filter === 'read') return c.isRead
  if (filter === 'starred') return c.isStarred
  if (filter === 'closed') return c.status === 'closed'
  if (filter === 'nearing-sla') return c.slaStatus === 'nearing'
  if (filter === 'beyond-sla') return c.slaStatus === 'breached'
  return true
}

export interface AdvancedFilters {
  priorityId: string
  categoryId: string
  subcategoryId: string
  typeId: string
  impactId: string
  urgencyId: string
  complexityId: string
  /** Lifecycle enum (open/on_hold/reopen/closed). */
  status: string
  /** Rows in the tenant's `conversation_status` catalog — multi-select; the
   *  list matches a conversation when its status is any of these. */
  conversationStatusIds: string[]
  campaignId: string
  assignedTeamId: string
  assignedAgentIds: string[]
  /** Agent list was picked from every agent, not narrowed by campaign/team. */
  allAgents: boolean
  locationId: string
  branchId: string
  departmentId: string
  referenceId: string
  clientId: string
  subject: string
  description: string
  stateId: string
  approvalStateId: string
  approvalLevelId: string
  dynamicProperty: string
  dynamicPropertyValue: string
}

const EMPTY_ADVANCED_FILTERS: AdvancedFilters = {
  allAgents: false,
  priorityId: '', categoryId: '', subcategoryId: '', typeId: '',
  impactId: '', urgencyId: '', complexityId: '', status: '', conversationStatusIds: [],
  campaignId: '', assignedTeamId: '', assignedAgentIds: [], locationId: '', branchId: '', departmentId: '',
  referenceId: '', clientId: '', subject: '', description: '',
  stateId: '', approvalStateId: '', approvalLevelId: '',
  dynamicProperty: '', dynamicPropertyValue: '',
}

export interface UseInboxConversationsResult {
  conversations: ConversationItem[]
  setConversations: React.Dispatch<React.SetStateAction<ConversationItem[]>>
  totalCount: number
  filterCounts: Record<string, number>
  /** Count per catalog status id, for the header's status chips. */
  statusCounts: Record<string, number>
  /** Statuses present in the data, for chips the active catalog doesn't list. */
  statusInfo: Array<{ id: string; name: string; color: string | null }>
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
  filter: InboxFilter
  setFilter: (filter: InboxFilter) => void
  search: string
  setSearch: (search: string) => void
  fromDate: string
  toDate: string
  setDateRange: (from: string, to: string) => void
  channel: string
  setChannel: (channel: string) => void
  campaignIds: string[]
  setCampaignIds: (ids: string[]) => void
  advancedFilters: AdvancedFilters
  setAdvancedFilters: (filters: Partial<AdvancedFilters>) => void
  clearAdvancedFilters: () => void
  page: number
  pageSize: number
  setPage: (page: number, pageSize: number) => void
  showChildTickets: boolean
  setShowChildTickets: (v: boolean) => void
  showSecondaryTickets: boolean
  setShowSecondaryTickets: (v: boolean) => void
  /** Shared filter params (search/date/channel/campaign/advanced) as a query
   *  string, WITHOUT the base scope query, the filter tab, or limit/offset.
   *  Used by the inbox page to fetch scope-badge counts for BOTH scopes with
   *  the same filters applied, so the badges reflect what the user filtered. */
  filterParams: string
  markRead: (ids: string[]) => void
  markUnread: (ids: string[]) => void
  toggleStar: (id: string) => void
  archive: (ids: string[]) => void
  unarchive: (ids: string[]) => void
  bulkArchive: (ids: string[]) => Promise<{ successCount: number; errorCount: number }>
  bulkUnarchive: (ids: string[]) => Promise<{ successCount: number; errorCount: number }>
}

/** `baseQuery` is an optional extra query (e.g. `assignedAgentId=…&status=open`) applied on every fetch.
 *  `initialFilter` seeds the active filter tab (e.g. dedicated Nearing/Beyond-SLA pages). */
export function useInboxConversations(
  baseQuery = '',
  initialFilter: InboxFilter = 'all',
  options?: { enabled?: boolean },
): UseInboxConversationsResult {
  const enabled = options?.enabled ?? true
  const [conversations, setConversations] = useState<ConversationItem[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [counts, setCounts] = useState<ApiCounts>(EMPTY_COUNTS)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [filter, setFilterState] = useState<InboxFilter>(initialFilter)
  const [search, setSearchState] = useState('')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [channel, setChannelState] = useState('')
  const [campaignIds, setCampaignIdsState] = useState<string[]>([])
  const [advancedFilters, setAdvancedFiltersState] = useState<AdvancedFilters>(EMPTY_ADVANCED_FILTERS)
  const [page, setPageState] = useState(1)
  const [pageSize, setPageSizeState] = useState(20)
  const [showChildTickets, setShowChildTicketsState] = useState(true)
  const [showSecondaryTickets, setShowSecondaryTicketsState] = useState(true)

  // Shared filter params (search/date/channel/campaign/advanced) WITHOUT the
  // base scope query, the filter tab, or limit/offset. Used by buildQuery,
  // refetchCounts AND the inbox page's scope-badge counts so all three stay in
  // sync — previously the scope badges ignored every filter, so "My
  // Conversations" / "Campaign Conversations" counts never changed when the
  // user filtered.
  const filterParams = useMemo(() => {
    const params = new URLSearchParams()
    if (search.trim()) params.set('search', search.trim())
    if (fromDate) params.set('from', fromDate)
    if (toDate) params.set('to', toDate)
    if (channel) params.set('channel', channel)
    if (campaignIds.length > 0) params.set('campaignIds', campaignIds.join(','))
    if (advancedFilters.priorityId) params.set('priorityId', advancedFilters.priorityId)
    if (advancedFilters.categoryId) params.set('categoryId', advancedFilters.categoryId)
    if (advancedFilters.subcategoryId) params.set('subcategoryId', advancedFilters.subcategoryId)
    if (advancedFilters.typeId) params.set('typeId', advancedFilters.typeId)
    if (advancedFilters.impactId) params.set('impactId', advancedFilters.impactId)
    if (advancedFilters.urgencyId) params.set('urgencyId', advancedFilters.urgencyId)
    if (advancedFilters.complexityId) params.set('complexityId', advancedFilters.complexityId)
    if (advancedFilters.status) params.set('status', advancedFilters.status)
    if (advancedFilters.conversationStatusIds.length > 0) params.set('conversationStatusIds', advancedFilters.conversationStatusIds.join(','))
    if (advancedFilters.campaignId) params.set('campaignId', advancedFilters.campaignId)
    if (advancedFilters.assignedTeamId) params.set('assignedTeamId', advancedFilters.assignedTeamId)
    if (advancedFilters.assignedAgentIds.length > 0) params.set('assignedAgentIds', advancedFilters.assignedAgentIds.join(','))
    if (advancedFilters.locationId) params.set('locationId', advancedFilters.locationId)
    if (advancedFilters.branchId) params.set('branchId', advancedFilters.branchId)
    if (advancedFilters.departmentId) params.set('departmentId', advancedFilters.departmentId)
    if (advancedFilters.referenceId) params.set('referenceId', advancedFilters.referenceId)
    if (advancedFilters.clientId) params.set('clientId', advancedFilters.clientId)
    if (advancedFilters.subject) params.set('subject', advancedFilters.subject)
    if (advancedFilters.description) params.set('description', advancedFilters.description)
    if (advancedFilters.stateId) params.set('stateId', advancedFilters.stateId)
    if (advancedFilters.approvalStateId) params.set('approvalStateId', advancedFilters.approvalStateId)
    if (advancedFilters.approvalLevelId) params.set('approvalLevelId', advancedFilters.approvalLevelId)
    if (advancedFilters.dynamicProperty) params.set('dynamicProperty', advancedFilters.dynamicProperty)
    if (advancedFilters.dynamicPropertyValue) params.set('dynamicPropertyValue', advancedFilters.dynamicPropertyValue)
    if (!showChildTickets) params.set('withoutParent', '1')
    if (!showSecondaryTickets) params.set('withoutMerged', '1')
    return params.toString()
  }, [search, fromDate, toDate, channel, campaignIds, advancedFilters, showChildTickets, showSecondaryTickets])

  const buildQuery = useCallback((): string => {
    const params = new URLSearchParams()
    if (baseQuery) {
      for (const pair of baseQuery.split('&')) {
        const [k, v] = pair.split('=')
        if (k && v !== undefined) params.set(k, decodeURIComponent(v))
      }
    }
    params.set('filter', filter)
    // Merge shared filter params
    const fp = new URLSearchParams(filterParams)
    for (const [k, v] of fp) params.set(k, v)
    params.set('limit', String(pageSize))
    params.set('offset', String((page - 1) * pageSize))
    return params.toString()
  }, [baseQuery, filter, filterParams, page, pageSize])

  const fetchIdRef = useRef(0)

  const refetch = useCallback(async (): Promise<void> => {
    const fetchId = ++fetchIdRef.current
    setLoading(true)
    setError(null)
    try {
      const res = await api.get<{ items: ApiConversation[]; totalCount: number }>(`/conversations?${buildQuery()}`)
      if (fetchId !== fetchIdRef.current) return // a newer request superseded this one
      setConversations((res.items ?? []).map(toItem))
      setTotalCount(res.totalCount ?? 0)
    } catch (e) {
      if (fetchId !== fetchIdRef.current) return
      setError(e instanceof Error ? e.message : 'Failed to load conversations')
      setConversations([])
      setTotalCount(0)
    } finally {
      if (fetchId === fetchIdRef.current) setLoading(false)
    }
  }, [buildQuery])

  const refetchCounts = useCallback(async (): Promise<void> => {
    const params = new URLSearchParams()
    if (baseQuery) {
      for (const pair of baseQuery.split('&')) {
        const [k, v] = pair.split('=')
        if (k && v !== undefined) params.set(k, decodeURIComponent(v))
      }
    }
    // Merge shared filter params (keeps counts in sync with the list, and
    // includes conversationStatusIds which was previously missing here).
    const fp = new URLSearchParams(filterParams)
    for (const [k, v] of fp) params.set(k, v)
    try {
      const res = await api.get<ApiCounts>(`/conversations/counts?${params.toString()}`)
      setCounts(res)
    } catch {
      // Badge counts are a non-critical nicety — leave the previous values on failure.
    }
  }, [baseQuery, filterParams])

  useEffect(() => {
    if (enabled) void refetch()
  }, [refetch, enabled])
  useEffect(() => {
    if (enabled) void refetchCounts()
  }, [refetchCounts, enabled])

  // A persisted/restored page can outlive the rows that made it valid
  // (archiving, reassignment, filters shrinking the result set). Once a
  // fetch has settled, land on the last real page rather than an empty slice.
  useEffect(() => {
    if (loading || !enabled) return
    const lastPage = Math.max(1, Math.ceil(totalCount / pageSize))
    if (page > lastPage) setPageState(lastPage)
  }, [loading, enabled, totalCount, page, pageSize])

  // Switching audience (e.g. Mine → All in the unified inbox) changes
  // `baseQuery`. Page 3 of "Mine" is meaningless in "All", so start over —
  // otherwise the switch can land on an empty page of a non-empty result set.
  const prevBaseQuery = useRef(baseQuery)
  useEffect(() => {
    if (prevBaseQuery.current === baseQuery) return
    prevBaseQuery.current = baseQuery
    setPageState(1)
  }, [baseQuery])

  // Realtime: the ws-gateway bumps `conversation:list:bump` on the tenant room
  // whenever a conversation is created/updated (incl. new child conversations and
  // parent/child relation changes). Debounce-refetch so the inbox — and its
  // parent/child badges — stay live without a manual refresh.
  useEffect(() => {
    if (!enabled) return
    const socket = getSocket()
    let t: ReturnType<typeof setTimeout> | null = null
    const onBump = (): void => {
      if (t) clearTimeout(t)
      t = setTimeout(() => { void refetch(); void refetchCounts() }, 400)
    }
    socket.on('conversation:list:bump', onBump)
    return () => {
      if (t) clearTimeout(t)
      socket.off('conversation:list:bump', onBump)
    }
  }, [enabled, refetch, refetchCounts])

  const setFilter = useCallback((next: InboxFilter) => { setFilterState(next); setPageState(1) }, [])
  const setSearch = useCallback((next: string) => { setSearchState(next); setPageState(1) }, [])
  const setDateRange = useCallback((from: string, to: string) => { setFromDate(from); setToDate(to); setPageState(1) }, [])
  const setChannel = useCallback((next: string) => { setChannelState(next); setPageState(1) }, [])
  const setCampaignIds = useCallback((ids: string[]) => { setCampaignIdsState(ids); setPageState(1) }, [])
  const setAdvancedFilters = useCallback((partial: Partial<AdvancedFilters>) => {
    setAdvancedFiltersState((prev) => ({ ...prev, ...partial })); setPageState(1)
  }, [])
  const clearAdvancedFilters = useCallback(() => {
    setAdvancedFiltersState(EMPTY_ADVANCED_FILTERS); setPageState(1)
  }, [])
  const setPage = useCallback((newPage: number, newPageSize: number) => { setPageState(newPage); setPageSizeState(newPageSize) }, [])
  const setShowChildTickets = useCallback((v: boolean) => { setShowChildTicketsState(v); setPageState(1) }, [])
  const setShowSecondaryTickets = useCallback((v: boolean) => { setShowSecondaryTicketsState(v); setPageState(1) }, [])

  const filterCounts: Record<string, number> = {
    all: counts.all,
    unread: counts.unread,
    read: counts.read,
    starred: counts.starred,
    closed: counts.closed,
    'nearing-sla': counts.nearingSla,
    'beyond-sla': counts.beyondSla,
    archived: counts.archived,
  }
  const statusCounts = counts.byStatus ?? {}
  const statusInfo = counts.statusInfo ?? []

  // Optimistically flip local state, persist to the API, and roll back on
  // failure so the row never lies about what's actually saved. Rows that no
  // longer match the active filter tab are dropped from view immediately;
  // counts are refreshed in the background rather than hand-computed deltas.
  const setRead = useCallback((ids: string[], isRead: boolean): void => {
    const idSet = new Set(ids)
    setConversations((prev) => prev
      .map((c) => idSet.has(c.id) ? { ...c, isRead } : c)
      .filter((c) => !idSet.has(c.id) || matchesFilter(c, filter)))
    for (const id of ids) {
      api.patch(`/conversations/${id}/read-state`, { isRead })
        .then(() => void refetchCounts())
        .catch(() => {
          setConversations((prev) => prev.map((c) => c.id === id ? { ...c, isRead: !isRead } : c))
          void refetchCounts()
        })
    }
  }, [filter, refetchCounts])

  const markRead = useCallback((ids: string[]) => setRead(ids, true), [setRead])
  const markUnread = useCallback((ids: string[]) => setRead(ids, false), [setRead])

  const toggleStar = useCallback((id: string): void => {
    const current = conversations.find((c) => c.id === id)
    if (!current) return
    const nextStarred = !current.isStarred
    setConversations((prev) => prev
      .map((c) => c.id === id ? { ...c, isStarred: nextStarred } : c)
      .filter((c) => c.id !== id || matchesFilter(c, filter)))
    api.patch(`/conversations/${id}/star`, { isStarred: nextStarred })
      .then(() => void refetchCounts())
      .catch(() => {
        setConversations((prev) => prev.map((c) => c.id === id ? { ...c, isStarred: !nextStarred } : c))
        void refetchCounts()
      })
  }, [conversations, filter, refetchCounts])

  const setArchived = useCallback((ids: string[], isArchived: boolean): void => {
    const idSet = new Set(ids)
    setConversations((prev) => prev
      .map((c) => idSet.has(c.id) ? { ...c, isArchived } : c)
      .filter((c) => !idSet.has(c.id) || matchesFilter(c, filter)))
    for (const id of ids) {
      api.patch(`/conversations/${id}/archive`, { isArchived })
        .then(() => void refetchCounts())
        .catch(() => {
          setConversations((prev) => prev.map((c) => c.id === id ? { ...c, isArchived: !isArchived } : c))
          void refetchCounts()
        })
    }
  }, [filter, refetchCounts])

  const archive = useCallback((ids: string[]) => setArchived(ids, true), [setArchived])
  const unarchive = useCallback((ids: string[]) => setArchived(ids, false), [setArchived])

  const bulkArchive = useCallback(async (ids: string[]): Promise<{ successCount: number; errorCount: number }> => {
    const idSet = new Set(ids)
    const result = await api.patch<{ successCount: number; errorCount: number; errors?: { conversationId: string; error: string }[] }>(
      '/conversations/archive',
      { conversationIds: ids, isArchived: true },
    )
    // Optimistically drop the successfully archived rows that no longer match
    // the active filter tab; failed ids are left untouched since their state
    // didn't actually change. A full success/failure split isn't returned per
    // id today (only aggregate counts + optional per-id errors) — when errors
    // are present, only ids named in `errors` are treated as not-archived.
    const failedIds = new Set((result.errors ?? []).map((e) => e.conversationId))
    setConversations((prev) => prev
      .map((c) => (idSet.has(c.id) && !failedIds.has(c.id) ? { ...c, isArchived: true } : c))
      .filter((c) => !idSet.has(c.id) || failedIds.has(c.id) || matchesFilter(c, filter)))
    void refetchCounts()
    return { successCount: result.successCount, errorCount: result.errorCount }
  }, [filter, refetchCounts])

  const bulkUnarchive = useCallback(async (ids: string[]): Promise<{ successCount: number; errorCount: number }> => {
    const idSet = new Set(ids)
    const result = await api.patch<{ successCount: number; errorCount: number; errors?: { conversationId: string; error: string }[] }>(
      '/conversations/archive',
      { conversationIds: ids, isArchived: false },
    )
    // Mirror bulkArchive: flip successful ids to un-archived and drop those that
    // no longer match the (archived) filter tab; failed ids stay put.
    const failedIds = new Set((result.errors ?? []).map((e) => e.conversationId))
    setConversations((prev) => prev
      .map((c) => (idSet.has(c.id) && !failedIds.has(c.id) ? { ...c, isArchived: false } : c))
      .filter((c) => !idSet.has(c.id) || failedIds.has(c.id) || matchesFilter(c, filter)))
    void refetchCounts()
    return { successCount: result.successCount, errorCount: result.errorCount }
  }, [filter, refetchCounts])

  return {
    conversations, setConversations, totalCount, filterCounts, statusCounts, statusInfo, loading, error, refetch,
    filter, setFilter, search, setSearch, fromDate, toDate, setDateRange, channel, setChannel, campaignIds, setCampaignIds, advancedFilters, setAdvancedFilters, clearAdvancedFilters, page, pageSize, setPage, filterParams,
    showChildTickets, setShowChildTickets, showSecondaryTickets, setShowSecondaryTickets,
    markRead, markUnread, toggleStar, archive, unarchive, bulkArchive, bulkUnarchive,
  }
}
